import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Truck, Package, ShoppingBag, Bell, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, resolveFileUrl } from "@/lib/api";
import { storeJsonLd } from "@/lib/schema";
import { addToCart, getCart } from "@/lib/cart";
import GMark from "@/components/GMark";
import { ReportListingLink } from "@/components/ReportListingDialog";
import VerifiedGateBadge from "@/components/VerifiedGateBadge";
import Seo from "@/components/Seo";

function currency(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
}
function StatusChip({ state, qty, mode = "exact" }) {
  // Chips overlay product photos. All variants use SOLID backgrounds so they
  // stay legible against dark or busy imagery — never a translucent tint.
  let cls, label;
  if (state === "coming_soon") { cls = "bg-amber-100 text-amber-900 border-amber-300"; label = "Coming soon"; }
  else if (state === "sold_out") { cls = "bg-stone-200 text-stone-700 border-stone-300"; label = "Sold out"; }
  else if (mode === "status_only") { cls = "bg-white text-[#3F674E] border-[#3F674E]/30"; label = "Available"; }
  else if (qty <= 3) {
    // Low stock — solid Butter card, not a translucent tint.
    cls = "bg-[#FFF3D6] text-[#8B6B18] border-[#E8B84A]";
    label = `Only ${qty} left`;
  } else if (mode === "low_only") { cls = "bg-white text-[#3F674E] border-[#3F674E]/30"; label = "Available"; }
  else { cls = "bg-white text-[#3F674E] border-[#3F674E]/30"; label = `${qty} available`; }
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shadow-sm ${cls}`}>{label}</span>;
}

export default function StorePublic({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [waitDlg, setWaitDlg] = useState({ open: false, product: null });
  const [waitForm, setWaitForm] = useState({ email: "", name: "" });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.category) set.add(p.category); });
    return Array.from(set);
  }, [products]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (cat !== "all" && (p.category || "") !== cat) return false;
      if (!needle) return true;
      const hay = `${p.name} ${p.description || ""} ${p.pot_size || ""} ${p.category || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [products, q, cat]);

  const refreshCart = () => setCartCount(getCart(slug).reduce((s, i) => s + i.quantity, 0));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/stores/${slug}`);
        setStore(data.store); setProducts(data.products); refreshCart();
      } catch { setStore(false); }
    })();
    const h = () => refreshCart();
    window.addEventListener("cart-changed", h);
    return () => window.removeEventListener("cart-changed", h);
    // eslint-disable-next-line
  }, [slug]);

  const buy = (p) => {
    addToCart(slug, p, 1);
    toast.success(`${p.name} added to cart`);
    refreshCart();
  };

  const joinWaitlist = async () => {
    try {
      await api.post("/waitlist", { product_id: waitDlg.product.id, email: waitForm.email, name: waitForm.name });
      toast.success("We'll email you when it's back");
      setWaitDlg({ open: false, product: null }); setWaitForm({ email: "", name: "" });
    } catch { toast.error("Could not join waitlist"); }
  };

  if (store === false) return <div className="min-h-screen flex items-center justify-center text-stone-600">Store not found.</div>;
  if (!store) return <div className="min-h-screen flex items-center justify-center text-stone-600">Loading…</div>;

  const brand = store.brand_color || "#3F674E";
  const availableCount = products.filter((p) => p.state === "available" && p.quantity > 0).length;
  const seoDesc = store.intro
    ? `${store.intro.slice(0, 155)}${store.intro.length > 155 ? "…" : ""}`
    : `Shop plants from ${store.business_name}${store.location ? ` in ${store.location}` : ""} on Gate + Gather. ${availableCount} available right now — order online, pay how you like, collect or have it delivered.`;

  const isIndexable = store?.indexable !== false; // undefined → true (backwards compat)

  return (
    <div className="min-h-screen bg-[#F5F0E5]">
      <Seo
        title={`${store.business_name}${store.location ? ` — ${store.location}` : ""}`}
        description={seoDesc}
        path={slugOverride ? `/` : `/g/${slug}`}
        image={store.cover_url ? resolveFileUrl(store.cover_url) : "/apple-touch-icon.svg"}
        noindex={!isIndexable}
        jsonLd={isIndexable ? storeJsonLd(store, products, slug) : null}
      />
      {/* Example-Gate return banner — shown ONLY on the official sample Gate
          (quail-lane). Genuine seller Gates never render this so their
          branding is respected. Compact, single-row, clearly separate from
          the seller's own visual identity. */}
      {slug === "quail-lane" && (
        <div className="bg-[#20251F] text-white/95" data-testid="sample-gate-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 min-h-10 py-2 flex items-center justify-between gap-3">
            <Link
              to="/"
              data-testid="sample-gate-back-link"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium hover:text-white transition-colors whitespace-nowrap"
            >
              <span aria-hidden="true">←</span> <span className="hidden sm:inline">Back to </span>Gate + Gather
            </Link>
            <p className="font-mono-label text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-white/70 whitespace-nowrap">
              <span className="hidden sm:inline">You're viewing an </span><span className="text-white/95">Example Gate</span>
            </p>
          </div>
        </div>
      )}
      <div className="relative h-56 sm:h-72 lg:h-80 overflow-hidden" style={{ background: brand }}>
        {store.cover_url ? (
          <img
            src={resolveFileUrl(store.cover_url)}
            alt={`${store.business_name || "Gate"} cover`}
            className="w-full h-full object-cover opacity-90"
            fetchpriority="high"
            decoding="async"
            width="1600"
            height="640"
          />
        ) : (
          <img
            src="https://images.unsplash.com/photo-1668962225017-12ef9d7981c2?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
            alt={`${store.business_name || "Gate"} cover`}
            className="w-full h-full object-cover opacity-90"
            fetchpriority="high"
            decoding="async"
            width="1600"
            height="640"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
        {/* Hero photograph now stands on its own. The Verified Gate treatment
            lives in the meta row below the identity row — a single, quieter
            trust signal instead of duplicated badges. */}
      </div>

      <header className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full border border-stone-200 bg-white overflow-hidden shrink-0">
              {store.logo_url ? <img src={resolveFileUrl(store.logo_url)} alt={`${store.business_name || "Gate"} logo`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="40" height="40" /> : null}
            </div>
            <h1
              className="font-serif-heading text-lg sm:text-xl leading-tight m-0 truncate"
              data-testid="store-business-name"
              title={store.business_name}
            >
              {store.business_name}
            </h1>
          </div>
          <Link to={slugOverride ? `/checkout` : `/g/${slug}/checkout`} data-testid="header-cart-link" className="shrink-0">
            <Button className="rounded-full text-white h-10 px-3 sm:px-4 text-sm" style={{ background: brand }} data-testid="cart-btn">
              <ShoppingBag className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Cart</span>{cartCount > 0 && <span className="ml-1.5 bg-white/25 rounded-full px-2 text-xs">{cartCount}</span>}
            </Button>
          </Link>
        </div>
      </header>

      {/* META ROW — Verified Gate + location live together on their own line
          below the identity row so the identity row stays clean on mobile
          and the two trust signals read as a single meta band. */}
      {(store.is_verified || store.location) && (
        <div className="border-b border-stone-200 bg-stone-50/60" data-testid="store-meta-row">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-stone-600">
            {store.is_verified && <VerifiedGateBadge compact />}
            {store.is_verified && store.location && <span className="text-stone-300" aria-hidden="true">·</span>}
            {store.location && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {store.location}</span>
            )}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {store.is_paused && (
          <div data-testid="store-paused-banner" className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="font-serif-heading text-lg text-amber-900">This Gate is currently closed — check back soon.</p>
            <p className="text-sm text-amber-800 mt-1">The grower isn't taking new orders right now. Your Notify Me subscriptions still work.</p>
          </div>
        )}
        {store.intro && <p className="text-base sm:text-lg text-stone-700 leading-relaxed mb-6 max-w-3xl" data-testid="store-intro">{store.intro}</p>}

        <div className="flex flex-wrap gap-3 mb-8 text-xs">
          {store.offers_pickup && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-stone-200"><Package className="w-3.5 h-3.5" /> Pickup {store.pickup_availability ? `— ${store.pickup_availability}` : ""}</span>}
          {store.offers_delivery && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-stone-200"><Truck className="w-3.5 h-3.5" /> Delivery {store.delivery_fee > 0 ? `— ${currency(store.delivery_fee)}` : ""}</span>}
        </div>

        {products.length > 6 && (
          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input data-testid="storefront-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plants…" className="pl-9 rounded-full bg-white" />
            </div>
            {categories.length > 1 && (
              <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                <button data-testid="cat-all" onClick={() => setCat("all")} className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${cat === "all" ? "bg-[#20251F] text-white border-[#20251F]" : "bg-white border-stone-200 text-stone-700"}`}>All</button>
                {categories.map((c) => (
                  <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)} className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${cat === c ? "bg-[#20251F] text-white border-[#20251F]" : "bg-white border-stone-200 text-stone-700"}`}>{c}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center text-stone-500 py-16">{products.length === 0 ? "Nothing available right now. Check back soon." : "No matches — try a different search."}</div>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-4 mb-6" data-testid="whats-available-heading">
              <h2 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight">What's available</h2>
              <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#6B6E67]" data-testid="available-count">
                {(() => {
                  const c = filtered.filter((p) => p.state === "available" && p.quantity > 0).length;
                  return c === 1 ? "1 for sale" : `${c} for sale`;
                })()}
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filtered.map((p) => (
              <div key={p.id} data-testid={`storefront-product-${p.id}`} className="bg-white rounded-2xl border border-stone-200 overflow-hidden group flex flex-col fade-in">
                <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden">
                  {p.photo_url ? <img src={resolveFileUrl(p.photo_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" width="400" height="300" /> : null}
                  <div className="absolute top-3 left-3"><StatusChip state={p.state} qty={p.quantity} mode={store.stock_visibility || "exact"} /></div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-editorial text-lg text-[#20251F]">{p.name}</h3>
                  {p.pot_size && <p className="text-xs text-stone-500 mt-0.5">{p.pot_size}</p>}
                  {p.description && <p className="text-xs text-stone-600 mt-2 line-clamp-2">{p.description}</p>}
                  <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                    <p className="font-mono-label text-lg font-medium">{currency(p.price)}</p>
                    {p.state === "available" && p.quantity > 0 ? (
                      <Button data-testid={`add-${p.id}`} onClick={() => buy(p)} className="rounded-full text-white h-10 px-5 text-sm" style={{ background: brand }}>Add</Button>
                    ) : p.state === "coming_soon" ? (
                      <Button data-testid={`waitlist-${p.id}`} variant="outline" onClick={() => setWaitDlg({ open: true, product: p })} className="rounded-full border-amber-300 text-amber-900 hover:bg-amber-50 h-10 px-4 text-xs">Notify me</Button>
                    ) : (
                      <Button data-testid={`notify-${p.id}`} variant="outline" onClick={() => setWaitDlg({ open: true, product: p })} className="rounded-full border-[#DED5C3] text-[#20251F] hover:bg-[#F5F0E5] h-10 px-4 text-xs">Notify me when available</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}
      </main>

      {/* Crawler-friendly context block. Every Gate storefront is otherwise
          almost entirely product tiles, which drops text-to-HTML ratio and word
          count. This block adds ~140 words of natural context — who runs the
          Gate, how buying works, what Gate + Gather is — visible to visitors AND
          to search engines. Layout: on desktop the two cards are equal-height,
          padded identically, with eyebrows, headings and body-copy start
          positions aligned across both. On mobile they stack naturally and
          equal-height is intentionally released so each card is only as tall
          as its own content. */}
      <section aria-label={`About ${store.business_name}`} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="gate-context-block">
        <div className="grid md:grid-cols-2 gap-6 md:items-stretch">
          {/* LEFT — About this Gate */}
          <div className="rounded-2xl border border-[#DED5C3] bg-white p-6 md:p-8 flex flex-col" data-testid="gate-context-left">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#3F674E]">About this Gate</p>
            <h2 className="font-serif-heading text-xl sm:text-2xl text-[#20251F] mt-3 leading-[1.2] md:min-h-[3.6rem]">
              You're buying directly from {store.business_name}.
            </h2>
            <p className="mt-4 text-sm text-stone-700 leading-relaxed">
              {store.business_name} is an independent New Zealand grower{store.location ? ` based in ${store.location}` : ""}. Everything on this Gate is grown, propagated or prepared by them.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              When you order, it goes directly to the grower to prepare for you.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              Collection and local delivery options are shown at checkout.
            </p>
          </div>

          {/* RIGHT — How Gate + Gather works. `mt-auto` on the closing tagline
              pins it to the bottom of the card so it stays anchored even when
              the two cards are equal height on desktop. */}
          <div className="rounded-2xl border border-[#DED5C3] bg-[#FFFCF6] p-6 md:p-8 flex flex-col" data-testid="gate-context-right">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#3F674E]">How Gate + Gather works</p>
            <h2 className="font-serif-heading text-xl sm:text-2xl text-[#20251F] mt-3 leading-[1.2] md:min-h-[3.6rem]">
              A Gate is one grower's storefront — not a marketplace.
            </h2>
            <p className="mt-4 text-sm text-stone-700 leading-relaxed">
              Gate + Gather gives independent growers a simple place to show what's available, take orders and keep track of stock.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              Each Gate belongs to the grower — their products, their prices and their pickup or delivery options.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              If something is sold out, tap <em>Notify me when available</em> and we'll let you know when it's back.
            </p>
            <p className="mt-auto pt-6 font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#6B6E67]" data-testid="gate-context-tagline">
              Your Gate. Your customers. Not a marketplace.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#DED5C3] mt-16" data-testid="storefront-footer">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 flex-wrap">
          <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#6B6E67]">
            Your Gate · Your customers · Not a marketplace
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <ReportListingLink storeSlug={slug} />
            <Link to="/policy/seller-policy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]" data-testid="store-footer-policy">Seller Policy</Link>
            <Link to="/policy/privacy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]" data-testid="store-footer-privacy">Privacy</Link>
            <Link to="/policy/terms" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]" data-testid="store-footer-terms">Terms</Link>
            <Link to="/" className="flex items-center gap-2 group" data-testid="storefront-footer-brand">
              <GMark size={28} className="rounded-md shrink-0 opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="font-serif-heading text-base">
                Gate<span className="brand-plus"> + </span>Gather
              </span>
            </Link>
          </div>
        </div>
      </footer>

      <Dialog open={waitDlg.open} onOpenChange={(v) => setWaitDlg({ open: v, product: v ? waitDlg.product : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif-heading text-2xl">Notify me when available</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">We'll email you when <strong>{waitDlg.product?.name}</strong> is back in stock. No spot is held — first come first served.</p>
          <div className="space-y-3">
            <div><Label>Your name (optional)</Label><Input data-testid="waitlist-name" value={waitForm.name} onChange={(e) => setWaitForm({ ...waitForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="waitlist-email" type="email" required value={waitForm.email} onChange={(e) => setWaitForm({ ...waitForm, email: e.target.value })} className="mt-1" /></div>
            <Button data-testid="waitlist-submit" disabled={!waitForm.email} onClick={joinWaitlist} className="w-full rounded-full text-white" style={{ background: brand }}><Bell className="w-4 h-4 mr-1.5" /> Email me when available</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
